import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ShoppingBag, IdCard, Truck, ReceiptText, MailWarning, ArrowRight, Clock, XCircle,
    Smartphone, Ticket, MessageCircle, ChevronLeft, ChevronRight, ShieldCheck, Mail,
    GraduationCap, ExternalLink,
} from 'lucide-react';
import { notify } from '../../components/Notify';
import { getProfileApi, resendEmailVerificationApi } from '../../api';
import { getUser, patchUser } from '../../auth/storage';
import { useCooldown, formatarCooldown } from '../../hooks/useCooldown';
import { abrirWhatsappSuporte, EMAIL_SUPORTE, FUNEMG_EAD_URL } from '../../config';
import funemgLogo from '../../assets/funemg-icone.png';
import bannerCinema from '../../assets/landing/cinema.jpg';
import bannerMeia from '../../assets/meiaEntrada.jpg';
import bannerCarteira from '../../assets/backgroundcarteira.jpg';
import './dashboard.css';

const ATALHOS = [
    { path: '/produtos', label: 'Produtos', desc: 'Emitir ou renovar sua carteira', Icon: ShoppingBag },
    { path: '/minha-carteirinha', label: 'Minha carteirinha', desc: 'Ver e baixar sua carteira', Icon: IdCard },
    { path: '/rastreamento', label: 'Rastreamento', desc: 'Acompanhar a via física', Icon: Truck },
    { path: '/meu-historico', label: 'Histórico', desc: 'Seus pagamentos', Icon: ReceiptText },
    { path: '/funemg', label: 'FUNEMG EAD', desc: 'Cursos e comprovante de matrícula', Icon: GraduationCap },
];

const INFORMATIVOS = [
    { Icon: ShieldCheck, titulo: 'Documento oficial', texto: 'Válida em todo o território nacional, com verificação por QR Code.' },
    { Icon: Smartphone, titulo: '100% digital', texto: 'Emissão e renovação sem sair de casa, direto pelo portal.' },
    { Icon: Clock, titulo: 'Aprovação simples', texto: 'Nossa equipe confere seus dados e libera a carteira rapidinho.' },
    { Icon: Ticket, titulo: 'Meia-entrada', texto: 'Use em cinemas, shows, teatros e eventos culturais e esportivos.' },
];

// Carrossel SÓ de imagens — auto-avança, pausa no hover, setas + dots. As
// informações ficam FORA (seções abaixo). Slide pode ter `acao` (clicável).
function Carrossel({ banners }) {
    const [i, setI] = useState(0);
    const [pausado, setPausado] = useState(false);

    useEffect(() => {
        if (pausado || banners.length <= 1) return undefined;
        const id = setInterval(() => setI((p) => (p + 1) % banners.length), 5000);
        return () => clearInterval(id);
    }, [pausado, banners.length]);

    const go = (n) => setI((n + banners.length) % banners.length);

    return (
        <div className="ueb-carro"
            onMouseEnter={() => setPausado(true)} onMouseLeave={() => setPausado(false)}>
            <div className="ueb-carro-track" style={{ transform: `translateX(-${i * 100}%)` }}>
                {banners.map((b, idx) => (
                    <div key={idx} className={`ueb-carro-slide${b.acao ? ' is-click' : ''}`} onClick={b.acao || undefined}>
                        <img src={b.img} alt={b.alt || ''} />
                    </div>
                ))}
            </div>

            {banners.length > 1 && (
                <>
                    <button className="ueb-carro-arrow ueb-carro-arrow--prev" onClick={() => go(i - 1)} aria-label="Anterior"><ChevronLeft size={18} /></button>
                    <button className="ueb-carro-arrow ueb-carro-arrow--next" onClick={() => go(i + 1)} aria-label="Próximo"><ChevronRight size={18} /></button>
                    <div className="ueb-carro-dots">
                        {banners.map((_, idx) => (
                            <button key={idx} className={idx === i ? 'is-on' : ''} onClick={() => go(idx)} aria-label={`Slide ${idx + 1}`} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

export default function Dashboard() {
    const navigate = useNavigate();
    const [user, setUserState] = useState(getUser());
    const cooldown = useCooldown('ueb_verify_resend_cooldown', 10);
    const [reenviando, setReenviando] = useState(false);
    const [statusCart, setStatusCart] = useState(null); // 'pendente' | 'reprovada' | null

    const nome = user?.nome || user?.nome_completo || user?.name || 'Estudante';
    const primeiroNome = String(nome).trim().split(' ')[0];
    const emailVerificado = !!user?.email_verificado_at;

    const BANNERS = [
        { img: bannerCinema, alt: 'Meia-entrada em cinemas e eventos', acao: () => navigate('/produtos') },
        { img: bannerMeia, alt: 'Meia-entrada garantida com a carteira UEB' },
        { img: bannerCarteira, alt: 'Sua carteira de estudante', acao: () => navigate('/minha-carteirinha') },
    ];

    useEffect(() => {
        let vivo = true;
        getProfileApi()
            .then((res) => {
                if (!vivo || !res?.data) return;
                const verificado = res.data.email_verificado_at || res.data.email_verified_at;
                if (verificado && !getUser()?.email_verificado_at) {
                    const novo = patchUser({ email_verificado_at: verificado });
                    setUserState(novo);
                }
                const det = res.data?.pendencias?.detalhes || {};
                const c = res.data?.carteirinha;
                if (det.digital_reprovada) setStatusCart('reprovada');
                else if (c?.tem_valida && c?.status_aprovacao === 'pendente') setStatusCart('pendente');
                else setStatusCart(null);
            })
            .catch(() => { /* backend ainda não expõe /profile — silencioso */ });
        return () => { vivo = false; };
    }, []);

    const reenviarConfirmacao = async () => {
        if (cooldown.isActive || reenviando) return;
        setReenviando(true);
        try {
            const res = await resendEmailVerificationApi();
            if (res?.data?.alreadyVerified) {
                const novo = patchUser({ email_verificado_at: new Date().toISOString() });
                setUserState(novo);
                notify('Seu email já está confirmado!');
            } else {
                notify('Enviamos um novo email de confirmação.');
                cooldown.start();
            }
        } catch (err) {
            if (err?.response?.status === 429 && err.response.data?.try_at) {
                cooldown.startAt(err.response.data.try_at);
            } else {
                notify(err?.response?.data?.error || 'Não foi possível reenviar agora.');
            }
        } finally {
            setReenviando(false);
        }
    };

    // CTA principal do hero muda conforme o estado da carteirinha.
    const cartaCta = statusCart === 'reprovada'
        ? { label: 'Ver o que corrigir', to: '/minha-carteirinha' }
        : statusCart === 'pendente'
            ? { label: 'Acompanhar análise', to: '/minha-carteirinha' }
            : { label: 'Ver minha carteirinha', to: '/minha-carteirinha' };

    return (
        <div className="ueb-dash">
            {/* HERO — painel da marca (verde), com CTA contextual */}
            <header className="ueb-dash-hero">
                <p className="ueb-dash-hi">
                    Olá, {primeiroNome} 👋
                    {statusCart === 'pendente' && <span className="ueb-hero-chip"><Clock size={13} /> Em análise</span>}
                    {statusCart === 'reprovada' && <span className="ueb-hero-chip is-rep"><XCircle size={13} /> Requer correção</span>}
                </p>
                <h1>Bem-vindo à sua carteira UEB</h1>
                <p className="ueb-dash-lead">Sua carteira de estudante, pagamentos e entregas — tudo num só lugar.</p>
                <div className="ueb-dash-hero-actions">
                    <button className="ueb-hero-btn" onClick={() => navigate(cartaCta.to)}>
                        <IdCard size={17} /> {cartaCta.label}
                    </button>
                    <button className="ueb-hero-btn ueb-hero-btn--ghost" onClick={() => navigate('/produtos')}>
                        <ShoppingBag size={17} /> Emitir ou renovar
                    </button>
                </div>
            </header>

            {statusCart === 'reprovada' && (
                <div className="palert palert--danger ueb-dash-verify" role="button" tabIndex={0} onClick={() => navigate('/minha-carteirinha')}>
                    <XCircle size={18} />
                    <div className="ueb-dash-verify-txt"><strong>Sua carteirinha foi reprovada.</strong> Veja o motivo e como corrigir.</div>
                    <button className="pbtn pbtn--soft pbtn--sm" onClick={(e) => { e.stopPropagation(); navigate('/minha-carteirinha'); }}>Ver motivo</button>
                </div>
            )}

            {statusCart === 'pendente' && (
                <div className="palert palert--warning ueb-dash-verify" role="button" tabIndex={0} onClick={() => navigate('/minha-carteirinha')}>
                    <Clock size={18} />
                    <div className="ueb-dash-verify-txt"><strong>Sua carteirinha está em análise.</strong> Avisaremos assim que for aprovada.</div>
                    <button className="pbtn pbtn--soft pbtn--sm" onClick={(e) => { e.stopPropagation(); navigate('/minha-carteirinha'); }}>Acompanhar</button>
                </div>
            )}

            {!emailVerificado && (
                <div className="palert palert--warning ueb-dash-verify">
                    <MailWarning size={18} />
                    <div className="ueb-dash-verify-txt">
                        <strong>Confirme seu email.</strong> Enviamos um link de confirmação
                        {user?.email ? <> para <strong>{user.email}</strong></> : ''}. Não recebeu?
                    </div>
                    <button className="pbtn pbtn--soft pbtn--sm" onClick={reenviarConfirmacao} disabled={reenviando || cooldown.isActive}>
                        {reenviando ? <span className="pspin" /> : cooldown.isActive ? formatarCooldown(cooldown.secondsLeft) : 'Reenviar'}
                    </button>
                </div>
            )}

            {/* FUNEMG EAD — parceria: acesso aos cursos + site do EAD */}
            <section className="ueb-dash-funemg">
                <img src={funemgLogo} alt="FUNEMG" className="ueb-dash-funemg-logo" />
                <div className="ueb-dash-funemg-txt">
                    <span className="ueb-dash-funemg-tag"><GraduationCap size={13} /> Parceria FUNEMG</span>
                    <h2>Cursos da FUNEMG inclusos na sua carteira</h2>
                    <p>Assista às aulas no site do EAD e baixe seu comprovante de matrícula direto no portal.</p>
                    <div className="ueb-dash-funemg-acoes">
                        <button className="ueb-dash-funemg-btn" onClick={() => navigate('/funemg')}>
                            <GraduationCap size={16} /> Meus cursos
                        </button>
                        <button className="ueb-dash-funemg-btn ueb-dash-funemg-btn--ghost"
                            onClick={() => FUNEMG_EAD_URL ? window.open(FUNEMG_EAD_URL, '_blank', 'noopener') : notify('O link do site da FUNEMG ainda não foi configurado.')}>
                            <ExternalLink size={16} /> Abrir site da FUNEMG
                        </button>
                    </div>
                </div>
            </section>

            {/* PAINEL — carrossel (destaque) + acesso rápido em lista ao lado */}
            <div className="ueb-dash-panel">
                <div className="ueb-dash-panel-main">
                    <Carrossel banners={BANNERS} />
                </div>
                <aside className="ueb-qa pcard">
                    <h2 className="ueb-qa-title">Acesso rápido</h2>
                    <div className="ueb-qa-list">
                        {ATALHOS.map((a) => {
                            const { label, desc, Icon } = a;
                            const abrir = a.externo
                                ? () => a.href && window.open(a.href, '_blank', 'noopener')
                                : () => navigate(a.path);
                            return (
                                <button
                                    key={a.path || a.label}
                                    className={`ueb-qa-item ${a.destaque ? 'ueb-qa-item--tele' : ''}`}
                                    onClick={abrir}
                                >
                                    <span className="ueb-qa-ico"><Icon size={19} /></span>
                                    <span className="ueb-qa-txt">
                                        <strong>{label}</strong>
                                        <small>{desc}</small>
                                    </span>
                                    <ArrowRight size={17} className="ueb-qa-arrow" />
                                </button>
                            );
                        })}
                    </div>
                </aside>
            </div>

            {/* VANTAGENS — faixa única com divisórias */}
            <h2 className="ueb-dash-sec">Vantagens da sua carteira</h2>
            <section className="ueb-dash-bene pcard">
                {INFORMATIVOS.map(({ Icon, titulo, texto }) => (
                    <div key={titulo} className="ueb-dash-bene-item">
                        <span className="ueb-dash-bene-ico"><Icon size={19} /></span>
                        <strong>{titulo}</strong>
                        <p>{texto}</p>
                    </div>
                ))}
            </section>

            {/* CONTATO — barra verde */}
            <section className="ueb-dash-contato">
                <div className="ueb-dash-contato-txt">
                    <h2>Precisa falar com a gente?</h2>
                    <p>Dúvidas sobre emissão, pagamento e entrega? Nosso atendimento está pronto para ajudar.</p>
                </div>
                <div className="ueb-dash-contato-btns">
                    <button className="ueb-hero-btn" onClick={abrirWhatsappSuporte}><MessageCircle size={17} /> WhatsApp</button>
                    <a className="ueb-hero-btn ueb-hero-btn--ghost" href={`mailto:${EMAIL_SUPORTE}`}><Mail size={17} /> E-mail</a>
                </div>
            </section>
        </div>
    );
}
